import React from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * LeaveWithoutUpdatingDialog is a React component that renders a dialog
 * to confirm whether the user wants to proceed without making updates.
 * It provides options to either cancel the action or proceed with 
 * duplicating or handling the resume. The dialog displays different
 * messages based on the `isDuplicating` prop.
 *
 * @param {function} onClose - Callback function to close the dialog.
 * @param {boolean} isDuplicating - Flag to determine if the action 
 *                                  involves duplicating a resume.
 * @param {function} handleProcessClicked - Callback function to 
 *                                          handle process when 
 *                                          duplicating.
 * @param {string} resumeName - Name of the resume involved.
 * @param {function} handleClicked - Callback function to be called
 *                                   when proceeding without duplication.
 * @returns {React.ReactElement} - The rendered confirmation dialog component.
 */

const LeaveWithoutUpdatingDialog = ({ onClose, isDuplicating, handleProcessClicked, resumeName, handleClicked }) => {
    const navigate = useNavigate();
    const { data, totalResume, tab, jobData, createNewResume } = location.state || { data: null, totalResume: 0, tab: '4', jobData: null, createNewResume: false };
    const [open, setOpen] = useState(true)
    const [loading, setLoading] = useState(false);

    /**
     * Closes the dialog by invoking the onClose callback function.
     */

    const handleCancel = () => {
        onClose();
    };

    /**
     * Handles the proceed action for the dialog.
     * Sets the loading state to true and determines the action to perform
     * based on whether the resume is being duplicated. If duplicating, 
     * invokes handleProcessClicked; otherwise, invokes handleClicked.
     */

    const handleProceed = async () => {
        setLoading(true);
        if (isDuplicating) {
            handleProcessClicked();
        } else {
            handleClicked();
        }
    };
    return (
        <Box className="ext-job-dialog-wraper remove-job-dialog" sx={{ padding: '24px 20px' }}>
            <Stack justifyContent={'center'}>
                {isDuplicating ? (
                    <Box sx={{ textAlign: 'center' }} mb={4}>
                        <Typography className="title" mb={0.5}>
                            Do you want a copy of this resume?
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }} mb={4}>
                        <Typography className="sub-title" mb={0.5}>A copy of your Master resume has been created. You can edit it as needed for specific roles or purposes
                        </Typography>
                    </Box>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={'center'} spacing={2.5}>
                    <Button fullWidth variant="outlined" size="medium" disableRipple disableElevation onClick={handleCancel}>Cancel</Button>
                    <Button className="continue-btn" fullWidth variant="contained" size="medium" disableRipple disableElevation onClick={handleProceed} disabled={loading}>{loading ? 'proceeding...' : ' proceed'}</Button>
                </Stack>
            </Stack>
        </Box>
    )
}

export default LeaveWithoutUpdatingDialog
